import * as bp from 'services/BpService'
import * as estate from 'services/estateService'
import * as portfolio from 'services/portfolioService'
import APIService from 'services/APIService'

const noListData = () => Promise.resolve({ data: { data: [] } })
const noop = () => Promise.resolve()

const accessApi = APIService()

// Look up whether an email already belongs to a platform account.
// Returns the matched user ({ id, email, firstName, lastName }) or null.
export const lookupUserByEmail = async (email) => {
  const clean = (email || '').trim().toLowerCase()
  if (!clean) return null
  try {
    const res = await accessApi.post('/users/check-emails', { emails: [clean] })
    const users = res?.data?.existingUsers || res?.data?.data?.existingUsers || []
    return users.find((u) => (u.email || '').toLowerCase() === clean) || users[0] || null
  } catch {
    return null
  }
}

const buildingScope = (id) => ({
  supportsDomainGrants: false,
  supportsRequests: true,
  supportsPerFile: true,
  listUsers: () => bp.getBpUsers(id),
  assignUser: (body) => bp.assignUserToBp({ bpId: id, body }),
  updateUser: (rowId, body) => bp.updateBpUser({ bpId: id, relationId: rowId, body }),
  revokeUser: (rowId) => bp.revokeAccessToBp({ bpId: id, relationId: rowId }),
  removeUserEverywhere: null,
  listInvitations: () => bp.getInvitationsListForBp(id),
  invite: (body) => bp.inviteUser({ bpId: id, body }),
  deleteInvitation: (invitationId) => bp.cancelInvitation({ bpId: id, invitationId }),
  listRevoked: noListData,
  listRequests: () => bp.getAccessRequests({ bpId: id }),
  acceptRequest: (requestId) => bp.acceptAccessRequest({ bpId: id, requestId }),
  denyRequest: (requestId, reason) => bp.denyAccessRequest({ bpId: id, requestId, reason }),
  listDomainGrants: noListData,
  getDomainGrantMembers: noListData,
  previewDomainGrant: () => Promise.resolve({ data: { data: {} } }),
  addDomainGrant: noop,
  removeDomainGrant: noop,
  restrictDomainUser: noop,
  liftDomainRestriction: noop,
})

const estateScope = (id) => ({
  supportsDomainGrants: true,
  supportsRequests: true,
  supportsPerFile: false,
  getTree: () => estate.getEstateAccessTree(id),
  listUsers: () => estate.listEstateUsers(id),
  assignUser: (body) => estate.assignEstateUser({ id, email: body.email, userId: body.userId, relationType: body.relationType, expiresAt: body.expiresAt, firstName: body.firstName, lastName: body.lastName }),
  updateUser: (rowId, body) => estate.updateEstateUser({ id, estateUserId: rowId, relationType: body.relationType }),
  revokeUser: (rowId) => estate.revokeEstateUser({ id, estateUserId: rowId }),
  removeUserEverywhere: (userId) => estate.removeUserFromEstate({ id, userId }),
  listInvitations: () => estate.listEstateInvitations(id),
  invite: (body) => estate.inviteEstateUser({ id, ...body }),
  deleteInvitation: (invitationId) => estate.deleteEstateInvitation({ id, invitationId }),
  listRevoked: () => estate.listEstateRevokedUsers(id),
  listRequests: () => estate.listEstateAccessRequests(id),
  acceptRequest: (requestId) => estate.acceptEstateAccessRequest({ id, requestId }),
  denyRequest: (requestId) => estate.deleteEstateAccessRequest({ id, requestId }),
  listDomainGrants: () => estate.listEstateDomainGrants(id),
  getDomainGrantMembers: (grantId) => estate.getEstateDomainGrantMembers(id, grantId),
  previewDomainGrant: (body) => estate.previewEstateDomainGrant(id, body),
  addDomainGrant: (body) => estate.addEstateDomainGrant(id, body),
  removeDomainGrant: (grantId) => estate.removeEstateDomainGrant(id, grantId),
  restrictDomainUser: (userId) => estate.restrictEstateDomainUser(id, userId),
  liftDomainRestriction: (userId) => estate.liftEstateDomainRestriction(id, userId),
})

const portfolioScope = (id) => ({
  supportsDomainGrants: true,
  supportsRequests: false,
  supportsPerFile: false,
  getTree: () => portfolio.getPortfolioAccessTree(id),
  listUsers: () => portfolio.listPortfolioUsers(id),
  assignUser: (body) => portfolio.assignPortfolioUser(id, body),
  updateUser: (rowId, body) => portfolio.updatePortfolioUser(id, rowId, body),
  revokeUser: (rowId) => portfolio.revokePortfolioUser(id, rowId),
  removeUserEverywhere: (userId) => portfolio.removeUserFromPortfolio(id, userId),
  listInvitations: () => portfolio.listPortfolioInvitations(id),
  invite: (body) => portfolio.assignPortfolioUser(id, body),
  deleteInvitation: (invitationId) => portfolio.deletePortfolioInvitation(id, invitationId),
  listRevoked: () => portfolio.listPortfolioRevokedUsers(id),
  listRequests: noListData,
  acceptRequest: noop,
  denyRequest: noop,
  listDomainGrants: () => portfolio.listPortfolioDomainGrants(id),
  getDomainGrantMembers: (grantId) => portfolio.getPortfolioDomainGrantMembers(id, grantId),
  previewDomainGrant: (body) => portfolio.previewPortfolioDomainGrant(id, body),
  addDomainGrant: (body) => portfolio.addPortfolioDomainGrant(id, body),
  removeDomainGrant: (grantId) => portfolio.removePortfolioDomainGrant(id, grantId),
  restrictDomainUser: (userId) => portfolio.restrictPortfolioDomainUser(id, userId),
  liftDomainRestriction: (userId) => portfolio.liftPortfolioDomainRestriction(id, userId),
})

export const makeAccessApi = (scopeType, scopeId) => {
  if (scopeType === 'estate') return estateScope(scopeId)
  if (scopeType === 'portfolio') return portfolioScope(scopeId)
  return buildingScope(scopeId)
}
